import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingBag, Calendar, Truck, Award, MapPin, Clock, Phone, ChevronRight, Star, ChevronLeft, Download, Smartphone, Camera } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import DoctorCard from '../components/DoctorCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [products, setProducts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [banners, setBanners] = useState([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [heroSlides, setHeroSlides] = useState([]);

  // Default slides (usado si no hay slides en la BD)
  const defaultSlides = [
    {
      image_url: 'https://customer-assets.emergentagent.com/job_c95dc577-b1d0-45f0-b961-5d64937cdad8/artifacts/dq0sxfy6_IMG_7763%202.jpeg',
      title: 'J3A Farmacias',
      subtitle: 'Medicamentos de calidad a los mejores precios',
      button_primary_text: 'Ver Productos',
      button_primary_link: '/productos',
      button_secondary_text: 'Agendar Cita',
      button_secondary_link: '/citas'
    },
    {
      image_url: 'https://customer-assets.emergentagent.com/job_c95dc577-b1d0-45f0-b961-5d64937cdad8/artifacts/u7l3ab5p_IMG_7759%202.jpeg',
      title: 'Promociones Especiales',
      subtitle: '¡Medicamentos 3x2! Tu salud nos importa',
      button_primary_text: 'Ver Ofertas',
      button_primary_link: '/productos',
      button_secondary_text: 'Más Info',
      button_secondary_link: '/productos'
    },
    {
      image_url: 'https://customer-assets.emergentagent.com/job_c95dc577-b1d0-45f0-b961-5d64937cdad8/artifacts/e4rf51u1_IMG_7762%202.jpeg',
      title: 'Nuestro Equipo',
      subtitle: 'Atención profesional y personalizada',
      button_primary_text: 'Conocer Más',
      button_primary_link: '/citas',
      button_secondary_text: 'Agendar Cita',
      button_secondary_link: '/citas'
    },
    {
      image_url: 'https://customer-assets.emergentagent.com/job_c95dc577-b1d0-45f0-b961-5d64937cdad8/artifacts/yszrsbjq_IMG_7765%202.jpeg',
      title: 'Consultas Médicas',
      subtitle: 'Online $50 | Presencial $75',
      button_primary_text: 'Agendar Cita',
      button_primary_link: '/citas',
      button_secondary_text: 'Ver Doctores',
      button_secondary_link: '/citas'
    }
  ];

  // Fetch hero slides from API
  useEffect(() => {
    const fetchHeroSlides = async () => {
      try {
        const res = await axios.get(`${API}/hero-slides?active_only=true`);
        if (res.data && res.data.length > 0) {
          setHeroSlides(res.data);
        } else {
          setHeroSlides(defaultSlides);
        }
      } catch (error) {
        console.log('Using default slides');
        setHeroSlides(defaultSlides);
      }
    };
    fetchHeroSlides();
  }, []);

  // Active slides to display
  const activeSlides = heroSlides.length > 0 ? heroSlides : defaultSlides;

  // Auto-rotate hero slides
  useEffect(() => {
    if (activeSlides.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [activeSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + activeSlides.length) % activeSlides.length);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-rotate banners
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBanner((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [banners.length]);

  const fetchData = async () => {
    try {
      const [productsRes, doctorsRes, bannersRes] = await Promise.all([
        axios.get(`${API}/products?limit=6`),
        axios.get(`${API}/doctors`),
        axios.get(`${API}/banners?active_only=true`)
      ]);
      setProducts(productsRes.data);
      setDoctors(doctorsRes.data);
      setBanners(bannersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const features = [
    {
      icon: ShoppingBag,
      title: 'Productos de Calidad',
      description: 'Medicamentos y productos de salud a los mejores precios',
      link: '/productos'
    },
    {
      icon: Calendar,
      title: 'Citas Médicas',
      description: 'Agenda tu consulta con nuestros doctores disponibles',
      link: '/citas'
    },
    {
      icon: Truck,
      title: 'Envío a Domicilio',
      description: 'Te llevamos tus medicamentos hasta tu puerta',
      link: '/productos'
    },
    {
      icon: Award,
      title: 'Programa de Puntos',
      description: '10 puntos por cada $110. A 100 puntos: consulta gratis o 20% de descuento',
      link: '/dashboard'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Estilo Tesla - Pantalla Completa */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Slides de fondo */}
        {activeSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Imagen de fondo */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${slide.image_url})` }}
            />
            {/* Overlay oscuro para mejor legibilidad */}
            <div className="absolute inset-0 bg-black/30" />
          </div>
        ))}

        {/* Contenido centrado */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
          <h1 
            className="text-5xl md:text-7xl lg:text-8xl font-bold mb-4 animate-fadeIn"
            style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}
          >
            {activeSlides[currentSlide]?.title}
          </h1>
          <p 
            className="text-xl md:text-2xl lg:text-3xl mb-8 font-medium"
            style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}
          >
            {activeSlides[currentSlide]?.subtitle}
          </p>
          
          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <Button
              onClick={() => navigate(activeSlides[currentSlide]?.button_primary_link || '/productos')}
              className="bg-[#E52225] hover:bg-[#C41C1F] text-white px-12 py-6 text-lg font-semibold rounded-lg min-w-[200px]"
              data-testid="hero-primary-btn"
            >
              {activeSlides[currentSlide]?.button_primary_text}
            </Button>
            {activeSlides[currentSlide]?.button_secondary_text && (
              <Button
                onClick={() => navigate(activeSlides[currentSlide]?.button_secondary_link || '/citas')}
                className="bg-white/90 hover:bg-white text-slate-900 px-12 py-6 text-lg font-semibold rounded-lg min-w-[200px]"
                data-testid="hero-secondary-btn"
              >
                {activeSlides[currentSlide]?.button_secondary_text}
              </Button>
            )}
          </div>
        </div>

        {/* Flechas de navegación - Estilo Tesla */}
        <button
          onClick={prevSlide}
          className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 group"
          aria-label="Anterior"
        >
          <ChevronLeft className="w-8 h-8 text-white group-hover:scale-110 transition-transform" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 group"
          aria-label="Siguiente"
        >
          <ChevronRight className="w-8 h-8 text-white group-hover:scale-110 transition-transform" />
        </button>

        {/* Indicadores de puntos - Abajo */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {activeSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-white w-8' 
                  : 'bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Ir al slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Info bar abajo */}
        <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/60 to-transparent py-4">
          <div className="container-app flex justify-center gap-8 text-white/90 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>8:00 AM - 8:00 PM</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <a href="tel:6143844739" className="hover:text-white">614 384 4739</a>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Chihuahua, México</span>
            </div>
          </div>
        </div>
      </section>

      {/* Promotional Banners */}
      {banners.length > 0 && (
        <section className="py-4 bg-white">
          <div className="container-app">
            <div className="relative overflow-hidden rounded-2xl">
              {/* Banner Content */}
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentBanner * 100}%)` }}
              >
                {banners.map((banner) => (
                  <div
                    key={banner.id}
                    className="min-w-full px-6 py-8 md:px-12 md:py-10 flex items-center justify-between"
                    style={{ 
                      backgroundColor: banner.background_color,
                      color: banner.text_color 
                    }}
                  >
                    <div className="flex-1">
                      <h3 className="text-2xl md:text-3xl font-bold mb-2">{banner.title}</h3>
                      {banner.subtitle && (
                        <p className="text-lg opacity-90">{banner.subtitle}</p>
                      )}
                    </div>
                    {banner.link_url && banner.link_text && (
                      <Button
                        onClick={() => navigate(banner.link_url)}
                        className="ml-4 bg-white text-slate-900 hover:bg-slate-100 font-semibold"
                        data-testid={`banner-cta-${banner.id}`}
                      >
                        {banner.link_text}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              {banners.length > 1 && (
                <>
                  <button
                    onClick={prevBanner}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors"
                    aria-label="Banner anterior"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                  <button
                    onClick={nextBanner}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors"
                    aria-label="Siguiente banner"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                </>
              )}

              {/* Dots Indicator */}
              {banners.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentBanner(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-colors ${
                        index === currentBanner ? 'bg-white' : 'bg-white/40'
                      }`}
                      aria-label={`Ir al banner ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="container-app">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                onClick={() => navigate(feature.link)}
                className="p-6 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-lg transition-all duration-300 group cursor-pointer"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#E52225] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-500">{feature.description}</p>
                <div className="mt-3 text-[#E52225] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  Ver más <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Points Banner - Solo para usuarios logueados */}
      {isAuthenticated && (
        <section className="py-8 bg-gradient-to-r from-green-500 to-green-600">
          <div className="container-app">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">¡Gana Puntos con tus Compras!</h3>
                  <p className="text-green-100">Sube la foto de tu ticket de compra y acumula puntos (10 pts por cada $110)</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/dashboard?tab=tickets')}
                className="bg-white text-green-600 hover:bg-green-50 font-bold px-8 py-4 text-lg shadow-lg"
                data-testid="upload-ticket-home-btn"
              >
                <Camera className="w-5 h-5 mr-2" />
                Subir Mi Ticket
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Products Section */}
      <section className="py-16 bg-slate-50">
        <div className="container-app">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-slate-900">Productos Destacados</h2>
              <p className="text-slate-500 mt-1">Los mejores precios en medicamentos</p>
            </div>
            <Link 
              to="/productos" 
              className="text-[#E52225] font-medium hover:underline flex items-center gap-1"
              data-testid="ver-todos-productos"
            >
              Ver todos <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card-product animate-pulse">
                  <div className="aspect-square bg-slate-200 rounded-xl mb-4" />
                  <div className="h-4 bg-slate-200 rounded w-20 mb-2" />
                  <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-slate-200 rounded w-full mb-3" />
                  <div className="h-6 bg-slate-200 rounded w-24" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Doctors Section */}
      <section className="py-16 bg-white">
        <div className="container-app">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-slate-900">Nuestros Doctores</h2>
              <p className="text-slate-500 mt-1">Médicos disponibles para atenderte</p>
            </div>
            <Link 
              to="/citas" 
              className="text-[#2E7DCD] font-medium hover:underline flex items-center gap-1"
              data-testid="ver-todos-doctores"
            >
              Agendar Cita <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-6">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="card-doctor animate-pulse">
                  <div className="flex gap-4">
                    <div className="w-20 h-20 bg-slate-200 rounded-xl" />
                    <div className="flex-1">
                      <div className="h-5 bg-slate-200 rounded w-32 mb-2" />
                      <div className="h-4 bg-slate-200 rounded w-24 mb-2" />
                      <div className="h-4 bg-slate-200 rounded w-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {doctors.slice(0, 2).map((doctor) => (
                <DoctorCard key={doctor.id} doctor={doctor} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Download App Section - QR Code Grande */}
      <section className="py-16 bg-white">
        <div className="container-app">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12 text-white overflow-hidden relative">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#2563EB] rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#E52225] rounded-full blur-3xl"></div>
            </div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-4">
                  <Smartphone className="w-5 h-5" />
                  <span className="text-sm font-medium">Descarga Nuestra App</span>
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold mb-3">
                  Lleva J3A Farmacias en tu bolsillo
                </h2>
                <p className="text-slate-300 max-w-2xl mx-auto">
                  Escanea el código QR con tu celular para instalar nuestra app. 
                  ¡Compártelo con familia y amigos!
                </p>
              </div>

              {/* QR Code Grande - Centro */}
              <div className="flex justify-center mb-8">
                <div className="bg-white p-8 rounded-3xl shadow-2xl" id="qr-container">
                  <QRCodeSVG
                    value="https://cash-manager-53.preview.emergentagent.com"
                    size={280}
                    level="H"
                    includeMargin={false}
                    bgColor="#ffffff"
                    fgColor="#1e293b"
                  />
                  <div className="text-center mt-4">
                    <p className="text-slate-800 font-bold text-lg">J3A Farmacias</p>
                    <p className="text-slate-500 text-sm">Escanea para descargar</p>
                  </div>
                  <div className="flex justify-center gap-2 mt-3">
                    <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">iOS</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">Android</span>
                  </div>
                </div>
              </div>

              {/* Botones de Compartir */}
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                <Button
                  onClick={() => {
                    const text = "¡Descarga la app de J3A Farmacias! 💊 Medicamentos, citas médicas y más. Escanea este link: https://cash-manager-53.preview.emergentagent.com";
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                  className="bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold px-8 py-3 text-lg"
                  data-testid="share-whatsapp-btn"
                >
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Compartir por WhatsApp
                </Button>
                
                <Button
                  onClick={() => {
                    const text = "¡Descarga la app de J3A Farmacias! 💊 https://cash-manager-53.preview.emergentagent.com";
                    if (navigator.share) {
                      navigator.share({
                        title: 'J3A Farmacias App',
                        text: text,
                        url: 'https://cash-manager-53.preview.emergentagent.com'
                      });
                    } else {
                      navigator.clipboard.writeText(text);
                      alert('¡Link copiado al portapapeles!');
                    }
                  }}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 font-semibold px-8 py-3 text-lg"
                  data-testid="share-other-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Copiar Link / Compartir
                </Button>
              </div>

              {/* Beneficios */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
                <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm">Acceso rápido a productos</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm">Agenda citas fácilmente</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm">Guarda tus recetas</span>
                </div>
                <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm">Acumula puntos</span>
                </div>
              </div>

              <p className="text-center text-slate-400 text-sm mt-6">
                Disponible para iPhone y Android. Funciona sin conexión.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white">
        <div className="container-app">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">Visítanos</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Dirección</p>
                    <p className="text-blue-100">Esquina con C. Guillermo Prieto Luján</p>
                    <p className="text-blue-100">Prta de Hierro 300-7, Los Portales</p>
                    <p className="text-blue-100 text-sm">31137 Chihuahua, Chih., México</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Horario</p>
                    <p className="text-blue-100">Lunes a Domingo: 8:00 AM - 8:00 PM</p>
                    <p className="text-blue-100 text-sm">Descanso: 2:00 PM - 3:00 PM</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Teléfono</p>
                    <a href="tel:6143844739" className="text-blue-100 hover:text-white">614 384 4739</a>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => window.open('https://maps.google.com/?q=Esquina+con+C+Guillermo+Prieto+Lujan+Prta+de+Hierro+300-7+Los+Portales+31137+Chihuahua+Mexico', '_blank')}
                className="mt-6 bg-white text-[#2E7DCD] hover:bg-blue-50"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Ver en Google Maps
              </Button>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_pharmacy-plus-10/artifacts/548dsovh_PHOTO-2026-02-21-11-38-01.jpg"
                alt="J3A Farmacias y Consultorios"
                className="w-full h-[300px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
